import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Upload, Trash2, Image as ImageIcon } from "lucide-react";

const BUCKET = "gallery";

const Photos = () => {
  const [photos, setPhotos] = useState<{ name: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPhotos = async () => {
    const { data, error } = await supabase.storage.from(BUCKET).list("", {
      sortBy: { column: "created_at", order: "asc" },
    });

    if (error) {
      console.error(error);
      setLoading(false);
      return;
    }

    const photoList = (data || [])
      .filter((f) => !f.id?.endsWith("/") && f.name !== ".emptyFolderPlaceholder")
      .map((f) => ({
        name: f.name,
        url: supabase.storage.from(BUCKET).getPublicUrl(f.name).data.publicUrl,
      }));

    setPhotos(photoList);
    setLoading(false);
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      const fileName = `${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
      const { error } = await supabase.storage.from(BUCKET).upload(fileName, file, {
        cacheControl: "3600",
        upsert: false,
      });
      if (error) throw error;
    });

    try {
      await Promise.all(uploadPromises);
      toast({ title: "Uploaded", description: `${files.length} photo(s) uploaded` });
      await fetchPhotos();
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (name: string) => {
    const { error } = await supabase.storage.from(BUCKET).remove([name]);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "Photo removed" });
      setPhotos((prev) => prev.filter((p) => p.name !== name));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold italic">Photos</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage gallery images</p>
        </div>
        <div>
          <Input
            type="file"
            accept="image/*"
            multiple
            id="photo-upload"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
          <Button asChild className="text-xs tracking-widest uppercase" disabled={uploading}>
            <label htmlFor="photo-upload" className="cursor-pointer">
              <Upload size={14} className="mr-2" />
              {uploading ? "Uploading…" : "Upload Photos"}
            </label>
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border/50">
        <CardContent className="p-6">
          {loading ? (
            <p className="text-muted-foreground text-sm">Loading photos…</p>
          ) : photos.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon size={48} className="mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground text-sm">No photos uploaded yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo.name} className="relative group aspect-square overflow-hidden border border-border/50">
                  <img
                    src={photo.url}
                    alt={photo.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      size="icon"
                      variant="destructive"
                      className="h-10 w-10"
                      onClick={() => handleDelete(photo.name)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Photos;
